import { useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Menu, Share2, X } from "lucide-react";
import { toast } from "sonner";

export const NAV_LINKS = [
  { to: "/apps", label: "Apps" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/pwa-apps", label: "PWA Apps" },
  { to: "/education", label: "Education" },
  { to: "/consultancy", label: "AI Consultancy" },
  { to: "/app-dev", label: "App Development" },
  { to: "/community", label: "Community" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <header data-testid="site-nav" className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="font-heading font-black text-lg tracking-tight text-silver">
          KLYX<span className="text-mauve">™</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-xs tracking-[0.15em] uppercase font-medium transition-colors duration-200 hover:text-white ${
                  isActive ? "text-mauve" : "text-zinc-200"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex items-center gap-2 border border-mauve/50 text-mauve-light px-5 py-2 text-xs tracking-[0.15em] uppercase font-medium transition-[background-color,color,transform] duration-200 hover:bg-mauve hover:text-white hover:-translate-y-0.5"
          >
            Start a project <ArrowUpRight size={14} />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-zinc-300 p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden glass border-t border-white/10 px-6 py-6 flex flex-col gap-5">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="text-sm tracking-[0.15em] uppercase text-zinc-300 hover:text-white transition-colors duration-200"
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contact" onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase text-mauve">
            Start a project
          </Link>
        </div>
      )}
    </header>
  );
}

export function Reveal({ children, delay = 0, className = "", y = 28 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function ShareButton({ title, url, testId = "share-button" }) {
  const share = async () => {
    const shareUrl = url || window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title, url: shareUrl });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={share}
      className="inline-flex items-center gap-2 border border-white/15 text-zinc-300 px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase font-medium hover:border-mauve hover:text-white transition-colors duration-200"
    >
      <Share2 size={14} /> Share
    </button>
  );
}

export function SectionDivider() {
  return (
    <div aria-hidden className="relative w-full h-[2px] bg-white/[0.07]">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: "10rem" }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-y-0 left-0 bg-mauve"
      />
    </div>
  );
}

export function SectionHeader({ eyebrow, title, testId }) {
  return (
    <Reveal>
      <div data-testid={testId} className="flex items-center gap-4 mb-6">
        <span className="h-[2px] w-10 bg-mauve shrink-0" />
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-mauve">{eyebrow}</p>
      </div>
      <h2 className="font-heading font-black text-4xl sm:text-6xl tracking-tighter text-silver max-w-3xl text-balance">
        {title}
      </h2>
    </Reveal>
  );
}

export function EditorialMarquee({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid="editorial-marquee" className="border-y border-white/10 py-5 overflow-hidden select-none">
      <Marquee speed={28} gradient={false} pauseOnHover>
        {items.map((item, i) => (
          <span key={`${item}-${i}`} className="flex items-center gap-24 mx-12">
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-zinc-300">{item}</span>
            <span className="text-mauve/60 text-[10px]">◆</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export function ParallaxImage({ src, alt = "", className = "", speed = 0.12 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${speed * 100}%`, `${speed * 100}%`]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img src={src} alt={alt} style={{ y, scale: 1.25 }} className="w-full h-full object-cover" />
    </div>
  );
}

export function CTABand({ cta }) {
  if (!cta) return null;
  return (
    <section data-testid="cta-band" className="bg-elevated spotlight">
      <SectionDivider />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <Reveal>
          <div className="flex items-center gap-4 mb-6">
            <span className="h-[2px] w-10 bg-mauve shrink-0" />
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-mauve">Next step</p>
          </div>
          <h2 className="font-heading font-black text-4xl sm:text-6xl tracking-tighter text-silver max-w-3xl text-balance">
            {cta.title}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-zinc-200 max-w-xl leading-relaxed">{cta.body}</p>
          <Link
            to="/contact"
            data-testid="cta-band-button"
            className="mt-10 inline-flex items-center gap-3 bg-mauve text-white px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold transition-[background-color,transform] duration-200 hover:bg-mauve-dark hover:-translate-y-0.5"
          >
            {cta.button} <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-surface">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="font-heading font-black text-2xl text-silver">
            KLYX<span className="text-mauve">™</span>
          </p>
          <p className="mt-4 text-sm text-zinc-300 max-w-xs leading-relaxed">
            The hub for a family of progressive web apps, an education studio, and an AI consultancy with engineering behind it.
          </p>
        </div>
        <div className="md:col-span-3">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-5">Studio</p>
          <div className="flex flex-col gap-3">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-zinc-200 hover:text-white transition-colors duration-200">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
        <div className="md:col-span-4">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-5">Contact</p>
          <a href="mailto:hello@klyxbs.com" className="text-sm text-mauve-light hover:text-white transition-colors duration-200">
            hello@klyxbs.com
          </a>
          <p className="mt-8 text-xs text-zinc-400">
            <Link to="/admin" data-testid="footer-admin-link" className="hover:text-mauve transition-colors duration-200">
              Studio login
            </Link>
          </p>
        </div>
      </div>
      <div data-testid="footer-copyright" className="border-t border-white/5 py-6 text-center text-[11px] font-mono tracking-[0.2em] uppercase text-zinc-400">
        © {new Date().getFullYear()} Klyx™ — Copyright © Kenneth L. Clarke, Nassau The Bahamas. All rights reserved.
      </div>
    </footer>
  );
}
